(ns quuppa.qsm.planner.view
  (:require [quuppa.qsm.ui-state.events :as-alias state-events]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [quuppa.peweb.query :as q]
            [quuppa.qsm.planner.events :as e]
            [quuppa.qsm.planner.subs :as su]
            [quuppa.qsm.planner.utils :as planner-utils]
            [quuppa.qsm.planner.overlays :as overlays]
            [quuppa.qsm.planner.polygon-editor :as polygon-editor]
            [quuppa.qsm.planner.background :as background]
            [quuppa.qsm.planner.tree :as tree]
            [quuppa.ui.icons :refer [icon]]
            [reagent.core :as r]
            ["rc-tree" :as RcTree]))


;; Add state for scale setting
(def scale-points (r/atom []))
(def scale-distance (r/atom nil))
(def pending-scale-ppm (r/atom nil))

;; State for polygon editing (routes, geofences, and units)
(def current-route-points (r/atom []))
(def current-geofence-points (r/atom []))
(def current-unit-points (r/atom []))

;; State for background movement and rotation
(def bg-move-active (r/atom nil))
(def bg-rotate-active (r/atom nil))
(def delete-confirm-id (r/atom nil))

(defn route-props-editor
  "Form fields for editing simulation path properties."
  [plan-id building-id level-id route-id]
  (let [plan  @(rf/subscribe [::su/site-plan plan-id])
        route (get-in plan [:imdf :venue :buildings building-id :levels level-id :routes route-id])]
    (when route
      (let [upd #(rf/dispatch [::e/update-route-props plan-id building-id level-id route-id %])]
        [:<>
         [:div.mb-2
          [:label.form-label.small.fw-semibold "Name"]
          [:input.form-control.form-control-sm
           {:value     (or (:name route) "")
            :on-change #(upd {:name (.. % -target -value)})}]]
         [:div.mb-2
          [:label.form-label.small.fw-semibold "Tag group"]
          [:input.form-control.form-control-sm
           {:value     (or (:taggroup route) "")
            :on-change #(upd {:taggroup (.. % -target -value)})}]]
         [:div.mb-2
          [:label.form-label.small.fw-semibold "Notes"]
          [:textarea.form-control.form-control-sm
           {:value     (or (:notes route) "")
            :rows      2
            :on-change #(upd {:notes (.. % -target -value)})}]]
         [:div.row.g-2.mb-2
          [:div.col
           [:label.form-label.small.fw-semibold "Num tags"]
           [:input.form-control.form-control-sm
            {:type "number" :min "1" :step "1"
             :value     (or (:numtags route) 1)
             :on-change #(upd {:numtags (js/parseInt (.. % -target -value))})}]]
          [:div.col
           [:label.form-label.small.fw-semibold "Velocity (m/s)"]
           [:input.form-control.form-control-sm
            {:type "number" :min "0" :step "0.1"
             :value     (or (:velocityms route) 0.5)
             :on-change #(upd {:velocityms (js/parseFloat (.. % -target -value))})}]]]
         [:div.row.g-2.mb-2
          [:div.col
           [:label.form-label.small.fw-semibold "Accel (m/s²)"]
           [:input.form-control.form-control-sm
            {:type "number" :min "0" :step "0.1"
             :value     (or (:accelerationms2 route) 0.5)
             :on-change #(upd {:accelerationms2 (js/parseFloat (.. % -target -value))})}]]
          [:div.col
           [:label.form-label.small.fw-semibold "Cluster size"]
           [:input.form-control.form-control-sm
            {:type "number" :min "1" :step "1"
             :value     (or (:clustersize route) 1)
             :on-change #(upd {:clustersize (js/parseInt (.. % -target -value))})}]]]
         [:div.mb-2
          [:label.form-label.small.fw-semibold "Cluster radius (m)"]
          [:input.form-control.form-control-sm
           {:type "number" :min "0" :step "0.1"
            :value     (or (:clusterradius route) 0)
            :on-change #(upd {:clusterradius (js/parseFloat (.. % -target -value))})}]]
         [:div.mb-2
          [:label.form-label.small.fw-semibold "Color"]
          [:input
           {:type      "color"
            :value     (or (:color route) "#e41a1c")
            :style     {:width "100%" :height "32px" :padding "2px 4px"
                        :border "1px solid #ced4da" :border-radius "4px" :cursor "pointer"}
            :on-change #(upd {:color (.. % -target -value)})}]]
         (for [[kw label] [[:enabled "Enabled"]
                            [:visible "Visible"]
                            [:locationlocked "Location locked"]
                            [:useoldtags "Use old tags"]]]
           ^{:key (name kw)}
           [:div.mb-1.form-check
            [:input.form-check-input
             {:type      "checkbox"
              :id        (str "prop-" (name kw) "-" route-id)
              :checked   (boolean (get route kw))
              :on-change #(upd {kw (.. % -target -checked)})}]
            [:label.form-check-label {:for (str "prop-" (name kw) "-" route-id)}
             label]])]))))

(defn edit-panel
  "Collapsible properties panel anchored to the right side of the map.
   Only rendered when the selected element has editable properties."
  [open?-atom selected-type selected-id plan-id building-id level-id]
  (let [open?     @open?-atom
        has-props (contains? #{"route" "geofence" "unit" "opening" "locator" "backgrounds-container"} selected-type)]
    (when has-props
      [:div {:style {:position "absolute" :top 0 :right 0 :height "100%"
                     :display "flex" :flex-direction "row"
                     :z-index 20 :pointer-events "none"}}
       ;; Content panel — visible when open
       (when open?
         [:div {:style {:width "280px" :height "100%"
                        :background "rgba(255,255,255,0.97)"
                        :border-left "1px solid #dee2e6"
                        :overflow-y "auto"
                        :padding "12px"
                        :pointer-events "auto"
                        :font-size "13px"}}
          [:div {:style {:display "flex" :justify-content "space-between"
                         :align-items "center" :margin-bottom "10px"}}
           [:strong {:style {:font-size "13px"}}
            (if (= selected-type "backgrounds-container") "Image Layers" "Properties")]
           [:button.btn-close {:style {:font-size "10px"}
                               :on-click #(reset! open?-atom false)}]]
          (case selected-type
            "route" [route-props-editor plan-id building-id level-id selected-id]
            "backgrounds-container" [background/background-images-list plan-id building-id level-id open?-atom]
            [:p.text-muted.small {:style {:margin 0}} "No editable properties."])])
       ;; Toggle tab — always visible when has-props
       [:div {:style {:width "30px" :height "100%"
                      :background (if open? "#e9ecef" "rgba(248,249,250,0.9)")
                      :border-left "1px solid #dee2e6"
                      :cursor "pointer" :pointer-events "auto"
                      :display "flex" :align-items "center" :justify-content "center"
                      :flex-shrink 0}
              :on-click #(swap! open?-atom not)}
        [:span {:style {:writing-mode "vertical-rl"
                        :font-size "10px" :color "#6c757d"
                        :user-select "none" :padding "6px 0"}}
         (if open?
           "› Close"
           (if (= selected-type "backgrounds-container") "‹ Layers" "‹ Props"))]]])))


(defn map-viewport
  "Reagent component for the interactive map viewport.
   Subscribes directly to plan data so it re-renders immediately when any data
   changes (e.g. newly auto-detected units/openings).  Uses r/with-let for
   wheel-listener lifecycle and local r/atom state for zoom/pan/dragging."
  [selected-node image-dimensions plan-id]
  (r/with-let
    [viewport-zoom     (r/atom 1.0)
     viewport-pan      (r/atom [0 0])
     viewport-dragging (r/atom false)
     drag-start        (atom {:x 0 :y 0})
     background-interaction (r/atom nil)
     node-ref          (atom nil)
     live-offset       (r/atom nil)
     live-rotation     (r/atom nil)
     wheel-handler     (fn [e]
                         (.preventDefault e)
                         (let [delta (.-deltaY e)
                               zoom-factor (if (pos? delta) 0.9 1.1)]
                           (swap! viewport-zoom
                                  (fn [current-zoom]
                                    (let [new-zoom (* current-zoom zoom-factor)]
                                      (if (and (>= new-zoom 0.1) (<= new-zoom 10))
                                        new-zoom
                                        current-zoom))))))
     ref-fn            (fn [node]
                         (let [old @node-ref]
                           (when (not= old node)
                             (when old (.removeEventListener old "wheel" wheel-handler))
                             (reset! node-ref node)
                             (when node (.addEventListener node "wheel" wheel-handler #js {:passive false})))))]
    (let [zoom     @viewport-zoom
          [pan-x pan-y] @viewport-pan
          dragging @viewport-dragging
          {:keys [width height]} @image-dimensions
          img-width  width
          img-height height
          plan     @(rf/subscribe [::su/site-plan plan-id])
          action   @(rf/subscribe [:nav/match :parameters :query :action])
          route-id-from-url    @(rf/subscribe [:nav/match :parameters :query :route-id])
          geofence-id-from-url @(rf/subscribe [:nav/match :parameters :query :geofence-id])
          unit-id-from-url     @(rf/subscribe [:nav/match :parameters :query :unit-id])
          background-id-from-url @(rf/subscribe [:nav/match :parameters :query :background-id])
          rectangleM      @(rf/subscribe [::su/rectangle-m])
          pixels-per-meter (or @(rf/subscribe [::su/pixels-per-meter]) 100)
          selected-type (or (:data-type @selected-node)
                            (cond
                              (and route-id-from-url (not= route-id-from-url "null")) "route"
                              (and geofence-id-from-url (not= geofence-id-from-url "null")) "geofence"
                              (and unit-id-from-url (not= unit-id-from-url "null")) "unit"
                              (and background-id-from-url (not= background-id-from-url "null")) "background"))
          selected-level-id (or (:level-id @selected-node)
                                @(rf/subscribe [:nav/match :parameters :query :level-id])
                                (when (= selected-type "level") (:data-id @selected-node)))
          selected-building-id (or (:building-id @selected-node)
                                   @(rf/subscribe [:nav/match :parameters :query :building-id]))
          selected-background-id (or (:background-id @selected-node)
                                     @(rf/subscribe [:nav/match :parameters :query :background-id]))
          ;; All backgrounds for the level, sorted by sort-order, for stacked rendering
          all-level-bgs @(rf/subscribe [::su/level-background-images plan-id selected-building-id selected-level-id])
          bg-move-mode? (= @bg-move-active selected-background-id)
          bg-rotate-mode? (= @bg-rotate-active selected-background-id)
          current-level-routes (some (fn [[_ building]]
                                       (when-let [level (get-in building [:levels selected-level-id])]
                                         (:routes level)))
                                     (:buildings (:venue (:imdf plan))))
          current-level-geofences (some (fn [[_ building]]
                                          (when-let [level (get-in building [:levels selected-level-id])]
                                            (:geofences level)))
                                        (:buildings (:venue (:imdf plan))))
          current-level-units (some (fn [[_ building]]
                                      (when-let [level (get-in building [:levels selected-level-id])]
                                        (:units level)))
                                    (:buildings (:venue (:imdf plan))))
          current-level-openings (some (fn [[_ building]]
                                         (when-let [level (get-in building [:levels selected-level-id])]
                                           (:openings level)))
                                       (:buildings (:venue (:imdf plan))))
          current-level-locators (some (fn [[_ building]]
                                         (when-let [level (get-in building [:levels selected-level-id])]
                                           (not-empty (:locators level))))
                                       (:buildings (:venue (:imdf plan))))
          [_bg-id background-asset] (if (= selected-type "background")
                                      (let [bg-id (or (:data-id @selected-node) background-id-from-url)]
                                        [bg-id (background/get-background-image-asset (:imdf plan) bg-id)])
                                      (background/get-first-background-for-level (:imdf plan) selected-level-id))
          current-offset (or (:offset-px background-asset) {:x 0 :y 0})
          current-rotation (or (:rotation-deg background-asset) 0)
          floorplan-url (when-let [asset-id (:asset-id background-asset)]
                          (str "/site-plan/background-assets/" asset-id))]
      (if floorplan-url
        [:div.map-viewport
         {:ref ref-fn
          :style {:width "100%"
                  :height "100%"
                  :position "relative"
                  :overflow "hidden"
                  :cursor (cond @background-interaction "grabbing"
                                bg-move-mode?           "move"
                                dragging                "grabbing"
                                :else                   "default")}
          :on-context-menu (fn [e]
                             ;; Disable browser context menu in this view
                             (.preventDefault e))
          :on-mouse-down (fn [e]
                           (cond
                             (= (.-button e) 2)
                             (do (.preventDefault e)
                                 (reset! viewport-dragging true)
                                 (reset! drag-start {:x (- (.-clientX e) pan-x)
                                                     :y (- (.-clientY e) pan-y)}))
                           ;; Right button + move mode
                           (and (= (.-button e) 0) bg-move-mode?)
                             (do (println "Start moving, bg move active" bg-move-mode?)
                           (reset! background-interaction {:mode     :move
                                                           :start-x  (.-clientX e)
                                                           :start-y  (.-clientY e)
                                                           :initial-offset current-offset})
                                 (reset! live-offset current-offset)
                                 (println "After moving, bg move active" @background-interaction))
                           ;; Right button + rotate mode
                             (and (= (.-button e) 0) bg-rotate-mode?)
                             (let [img-el   (.querySelector (.-currentTarget e) "img")
                                   rect     (if img-el
                                              (.getBoundingClientRect img-el)
                                              (.getBoundingClientRect (.-currentTarget e)))
                                   cx       (+ (.-left rect) (/ (.-width rect) 2))
                                   cy       (+ (.-top rect) (/ (.-height rect) 2))
                                   dx       (- (.-clientX e) cx)
                                   dy       (- (.-clientY e) cy)
                                   start-angle (js/Math.atan2 dy dx)]
                               (reset! background-interaction {:mode             :rotate
                                                               :center-x         cx
                                                               :center-y         cy
                                                               :start-angle      start-angle
                                                               :initial-rotation current-rotation})
                               (reset! live-rotation current-rotation))
                             :else (fn [])))
          :on-mouse-move (fn [e]
                           (cond
                             dragging
                             (let [new-pan-x (- (.-clientX e) (:x @drag-start))
                                   new-pan-y (- (.-clientY e) (:y @drag-start))]
                               (reset! viewport-pan [new-pan-x new-pan-y]))
                             (= (:mode @background-interaction) :move)
                             (let [{:keys [start-x start-y initial-offset]} @background-interaction
                                   dx          (/ (- (.-clientX e) start-x) zoom)
                                   dy          (/ (- (.-clientY e) start-y) zoom)
                                   raw-x       (+ (:x initial-offset) dx)
                                   raw-y       (+ (:y initial-offset) dy)
                                   ;; Corrected clamping — image must stay within the container box:
                                   iw        (or (:width @image-dimensions) img-width)
                                   ih        (or (:height @image-dimensions) img-height)
                                   ;; Container dimensions (the stacked div)
                                   cw        img-width
                                   ch        img-height
                                   ;; Allow moving so at least 30% of the image overlaps the container
                                   margin-x  (* iw 0.30)
                                   margin-y  (* ih 0.30)
                                   max-x     (- cw margin-x)    ; rightmost: image right edge at 30% from container right
                                   min-x     (- margin-x iw)    ; leftmost: image left edge at 30% from container left
                                   max-y     (- ch margin-y)
                                   min-y     (- margin-y ih)
                                   clamped-x (max min-x (min max-x raw-x))
                                   clamped-y (max min-y (min max-y raw-y))]
                               (reset! live-offset {:x clamped-x :y clamped-y}))

                             (= (:mode @background-interaction) :rotate)
                             (let [{:keys [center-x center-y start-angle initial-rotation]} @background-interaction
                                   dx            (- (.-clientX e) center-x)
                                   dy            (- (.-clientY e) center-y)
                                   current-angle (js/Math.atan2 dy dx)
                                   raw-delta     (- current-angle start-angle)
                                   ;; Normalize to [-π, π] to avoid 360° jumps
                                   normalized    (- raw-delta (* 2 js/Math.PI (js/Math.round (/ raw-delta (* 2 js/Math.PI)))))
                                   delta-deg     (* (/ normalized js/Math.PI) 180)
                                   new-rotation  (mod (+ initial-rotation delta-deg) 360)]
                               (reset! live-rotation new-rotation))
                             :else (fn [])))
          :on-mouse-up (fn [e]
                         (when (= (.-button e) 2)
                           (reset! viewport-dragging false))
                         (when @background-interaction
                           (let [mode (:mode @background-interaction)]
                             (cond
                               (= mode :move)
                               (do (rf/dispatch [::e/update-background-offset
                                                 {:background-id selected-background-id
                                                  :asset-id      (:asset-id background-asset)
                                                  :offset-px     @live-offset}])
                                   (reset! live-offset nil))
                               (= mode :rotate)
                               (do (rf/dispatch [::e/update-background-rotation
                                                 {:background-id selected-background-id
                                                  :asset-id      (:asset-id background-asset)
                                                  :rotation-deg  @live-rotation}])
                                   (reset! live-rotation nil))))
                           (reset! background-interaction nil)
                           (when bg-move-mode? (reset! bg-move-active nil))
                           (when bg-rotate-mode? (reset! bg-rotate-active nil))))
          :on-mouse-leave (fn [_e]
                            (when dragging
                              (reset! viewport-dragging false))
                            (when @background-interaction (reset! background-interaction nil)))}
         [:div.site-plan__map-container
          {:style {:transform (str "translate(" pan-x "px, " pan-y "px) scale(" zoom ")")
                   :transform-origin "0 0"
                   :position "relative"
                   :pointer-events (if bg-move-mode? "none" "auto")
                   :width "fit-content"
                   :transition (if dragging "none" "transform 0.1s ease-out")}}
           [:div {:style {:position "relative"
                          :width    (str img-width "px")
                          :height   (str img-height "px")}}
            (doall
              (for [[idx [bg-id bg-asset]] (map-indexed vector all-level-bgs)]
                (let [asset-id     (:asset-id bg-asset)
                      bg-url       (when asset-id (str "/site-plan/background-assets/" asset-id))
                      selected?    (= bg-id selected-background-id)
                      bg-offset    (or (:offset-px bg-asset) {:x 0 :y 0})
                      bg-rotation  (or (:rotation-deg bg-asset) 0)
                      display-offset   (if (and selected? @live-offset) @live-offset bg-offset)
                      display-rotation (if (and selected? @live-rotation) @live-rotation bg-rotation)]
                  (when bg-url
                    [:div {:key   bg-id
                           :style {:position        "absolute"
                                   :top             0
                                   :left            0
                                   :z-index         (or (:sort-order bg-asset) idx)
                                   :opacity         (if selected? 1.0 0.55)
                                   :outline         (when selected? "2px solid #4a90e2")
                                   :outline-offset  "2px"
                                   :transform       (str "translate(" (:x display-offset) "px, "
                                                         (:y display-offset) "px) "
                                                         "rotate(" display-rotation "deg)")
                                   :transform-origin "center center"
                                   :transition      "opacity 0.15s ease, outline 0.1s ease"
                                   :pointer-events  (if (or bg-move-mode? bg-rotate-mode?) "none" "auto")}}
                     [:img {:src bg-url
                            :on-load (fn [e]
                                       (let [img (.-target e)
                                             natural-width (.-naturalWidth img)
                                             natural-height (.-naturalHeight img)
                                             current @image-dimensions]
                                         ;; Only update if dimensions actually changed
                                         (when (or (not= (:width current) natural-width)
                                                   (not= (:height current) natural-height))
                                           (reset! image-dimensions {:width natural-width :height natural-height}))))
                            :on-click (fn [e]
                                        (when (and (not dragging)
                                                   (= action "set-scale")
                                                   (nil? @pending-scale-ppm))
                                          (let [current-zoom @viewport-zoom
                                                rect (.getBoundingClientRect (.-currentTarget e))
                                                x (/ (- (.-clientX e) (.-left rect)) current-zoom)
                                                y (/ (- (.-clientY e) (.-top rect)) current-zoom)]
                                            (swap! scale-points
                                                   (fn [points]
                                                     (if (< (count points) 2)
                                                       (conj points [x y])
                                                       points))))))
                            :style (merge {:display "block"
                                           :user-select "none"
                                           :pointer-events (if bg-move-mode? "none" "auto")}
                                          (when rectangleM
                                            (planner-utils/rectM->css rectangleM pixels-per-meter)))}]

                     (when selected?
                       [:div {:style {:position        "absolute"
                                      :top             "-52px"
                                      :left            0
                                      :transform       (str "translateX(-50%) rotate(" (- current-rotation) "deg)")
                                      :transform-origin "center bottom"
                                      :cursor          "grab"
                                      :display         "flex"
                                      :flex-direction  "column"
                                      :align-items     "center"
                                      :pointer-events  "auto"
                                      :user-select     "none"}
                              :on-mouse-down (fn [e]
                                               (.stopPropagation e)
                                               (.preventDefault e)
                                               (let [img-div (.. e -currentTarget -parentElement)
                                                     rect    (.getBoundingClientRect img-div)
                                                     cx      (+ (.-left rect) (/ (.-width rect) 2))
                                                     cy      (+ (.-top rect) (/ (.-height rect) 2))
                                                     dx      (- (.-clientX e) cx)
                                                     dy      (- (.-clientY e) cy)]
                                                 (reset! bg-rotate-active bg-id)
                                                 (reset! background-interaction
                                                         {:mode             :rotate
                                                          :center-x         cx
                                                          :center-y         cy
                                                          :start-angle      (js/Math.atan2 dy dx)
                                                          :initial-rotation current-rotation})))}
                        [:div {:style {:width "1px" :height "28px" :background "rgba(74,144,226,0.8)"}}]
                        [:div {:style {:width           "22px"
                                       :height          "22px"
                                       :border-radius   "50%"
                                       :background      "white"
                                       :border          "1.5px solid #4a90e2"
                                       :box-shadow      "0 2px 6px rgba(0,0,0,0.25)"
                                       :display         "flex"
                                       :align-items     "center"
                                       :justify-content "center"}}
                         [:svg {:width "12" :height "12" :viewBox "0 0 24 24" :fill "none"
                                :stroke "#4a90e2" :stroke-width "2.5"
                                :stroke-linecap "round" :stroke-linejoin "round"}
                          [:path {:d "M21 2v6h-6"}]
                          [:path {:d "M3 12a9 9 0 0 1 15-6.7L21 8"}]
                          [:path {:d "M3 22v-6h6"}]
                          [:path {:d "M21 12a9 9 0 0 1-15 6.7L3 16"}]]]])]))))]
          ;; Always show all routes for the current level (inside map-container so they scale)
          (when current-level-routes
            (let [selected-route-id (when (= selected-type "route")
                                      (or (:data-id @selected-node) route-id-from-url))]
              [overlays/routes-display-overlay current-level-routes selected-route-id img-width img-height]))
          ;; Always show all geofences for the current level (inside map-container so they scale)
          (when current-level-geofences
            (let [selected-geofence-id (when (= selected-type "geofence")
                                         (or (:data-id @selected-node) geofence-id-from-url))]
              [overlays/geofences-display-overlay current-level-geofences selected-geofence-id img-width img-height]))
          ;; Always show all units for the current level (inside map-container so they scale)
          (when current-level-units
            (let [selected-unit-id (when (= selected-type "unit")
                                     (or (:data-id @selected-node) unit-id-from-url))]
              [overlays/units-display-overlay current-level-units selected-unit-id img-width img-height]))
          ;; Always show all openings for the current level (inside map-container so they scale)
          (when current-level-openings
            [overlays/openings-display-overlay current-level-openings img-width img-height])
          ;; Always show all locators for the current level (inside map-container so they scale)
          (when current-level-locators
            (let [selected-locator-id (when (= selected-type "locator")
                                        (:data-id @selected-node))]
              [overlays/locators-display-overlay current-level-locators img-width img-height selected-locator-id]))
          ;; Show editing controls only when a route is selected
          (when (= selected-type "route")
            (let [selected-route-id (or (:data-id @selected-node) route-id-from-url)
                  selected-route (get current-level-routes selected-route-id)
                  route-color (or (:color selected-route)
                                  (planner-utils/get-route-color selected-route
                                                   (count (take-while #(not= (first %) selected-route-id)
                                                                       (vec current-level-routes)))))
                  stored-db-points (:points selected-route)]
              ^{:key selected-route-id}
              [polygon-editor/polygon-editing-overlay "route" img-width img-height plan-id selected-building-id selected-level-id selected-route-id route-color stored-db-points current-route-points ::e/update-route-points 0]))
          ;; Show editing controls only when a geofence is selected
          (when (= selected-type "geofence")
            (let [selected-geofence-id (or (:data-id @selected-node) geofence-id-from-url)
                  selected-geofence (get current-level-geofences selected-geofence-id)
                  geofence-color (or (:color selected-geofence)
                                     (planner-utils/get-geofence-color selected-geofence
                                                         (count (take-while #(not= (first %) selected-geofence-id)
                                                                             (vec current-level-geofences)))))
                  stored-db-points (:points selected-geofence)]
              ^{:key selected-geofence-id}
              [polygon-editor/polygon-editing-overlay "geofence" img-width img-height plan-id selected-building-id selected-level-id selected-geofence-id geofence-color stored-db-points current-geofence-points ::e/update-geofence-points 0.2]))
          ;; Show editing controls only when a unit is selected
          (when (= selected-type "unit")
            (let [selected-unit-id (or (:data-id @selected-node) unit-id-from-url)
                  selected-unit (get current-level-units selected-unit-id)
                  unit-color (or (:color selected-unit)
                                 (planner-utils/get-unit-color selected-unit
                                                 (count (take-while #(not= (first %) selected-unit-id)
                                                                     (vec current-level-units)))))
                  stored-db-points (:points selected-unit)]
              ^{:key selected-unit-id}
              [polygon-editor/polygon-editing-overlay "unit" img-width img-height plan-id selected-building-id selected-level-id selected-unit-id unit-color stored-db-points current-unit-points ::e/update-unit-points 0.2]))
          ;; Scale-setting visual: inside container so it zooms/pans with the image
          (when (= action "set-scale")
            [polygon-editor/scale-points-visual-overlay img-width img-height @scale-points])]]
        [:div.text-center.p-5
         [:p.text-muted "Select a background image from the tree to view the map."]]))
    (finally
      (when-let [n @node-ref]
        (.removeEventListener n "wheel" wheel-handler)))))

(defn plan-edit [_plan-id]
  (let [selected-node       (r/atom nil)
        selected-node-type  (r/atom nil)   ;; tracks data-type explicitly to avoid closure/coercion issues
        image-dimensions    (r/atom {:width 800 :height 600})
        help-open?          (r/atom false)
        edit-panel-open?    (r/atom false)]
    (fn [plan-id]
      (let [action @(rf/subscribe [:nav/match :parameters :query :action])
            route-id-from-url @(rf/subscribe [:nav/match :parameters :query :route-id])
            geofence-id-from-url @(rf/subscribe [:nav/match :parameters :query :geofence-id])
            unit-id-from-url @(rf/subscribe [:nav/match :parameters :query :unit-id])
            selected-background-id @(rf/subscribe [:nav/match :parameters :query :background-id])
            ;; Use local image dimensions (set on image load)
            {:keys [width height]} @image-dimensions
            img-width width
            img-height height
            plan @(rf/subscribe [::su/site-plan plan-id])
            pixels-per-meter (or @(rf/subscribe [::su/pixels-per-meter]) 100)
            ;; Get background based on selection - show first background for level if level is known
            ;; If route-id, geofence-id, or unit-id is in URL (and not "null" string from bad URL state), treat it as selected
            selected-type (or @selected-node-type
                              (cond
                                (and route-id-from-url (not= route-id-from-url "null")) "route"
                                (and geofence-id-from-url (not= geofence-id-from-url "null")) "geofence"
                                (and unit-id-from-url (not= unit-id-from-url "null")) "unit"
                                (and selected-background-id (not= selected-background-id "null")) "background"))
          selected-level-id (or (:level-id @selected-node)
                                @(rf/subscribe [:nav/match :parameters :query :level-id])
                                (when (= selected-type "level") (:data-id @selected-node)))
          selected-building-id (or (:building-id @selected-node)
                                   @(rf/subscribe [:nav/match :parameters :query :building-id]))
          current-level-routes (some (fn [[_ building]]
                                       (when-let [level (get-in building [:levels selected-level-id])]
                                         (:routes level)))
                                     (:buildings (:venue (:imdf plan))))
          selected-id (cond
                        (= selected-type "route")   (or (:data-id @selected-node) route-id-from-url)
                        (= selected-type "geofence") (or (:data-id @selected-node) geofence-id-from-url)
                        (= selected-type "unit")    (or (:data-id @selected-node) unit-id-from-url)
                        :else                        (:data-id @selected-node))
          background-asset (when (and selected-background-id selected-level-id selected-building-id)
                             (get-in plan [:imdf :venue :buildings selected-building-id
                                           :levels selected-level-id
                                           :siteresource-backgroundimages selected-background-id]))
         bg-move-mode? (= @bg-move-active selected-background-id)
         scale-pts @scale-points
         pending-ppm @pending-scale-ppm
         ; NOTE probably would make sense to support a separate, perhaps markdown formatted csv file for inline help text with a help-id and content
         help-content (case selected-type
                         "background"
                         (str "After adding a new background or a floorplan image, "
                              "first adjust its position and orientation in relation to the other background "
                              "images you may have for this level. Then, consider the boundaries of "
                              "the tracking area and whether your background image describes any units "
                              "(typically rooms for indoor floorplans) and any openings in between them. You can draw all "
                              "of this manually, but it's easiest to get started by clicking on \"Auto-detect "
                              "units & openings\" which will provide the initial, AI-assisted analysis of the "
                              "physical elements in the image. You can then add, delete and adjust the unit "
                              "polygons and openings as needed. Once done with that, you can choose the tracking "
                              "area (a specific geofence) and distribute locators on this level.")
                         nil)]
        [:<>
         ;; Load rc-tree CSS
         [:link {:rel "stylesheet"
                 :type "text/css"
                 :href "/css/rc-tree.css"}]
         ;; Responsive help panel / button positioning
         [:style "
           .planner-help-panel { right: 16px; }
           .planner-help-btn {
             border-radius: 4px 4px 0 0 !important;
             border-bottom-color: #f8f9fa !important;
             margin-bottom: -1px;
           }
           .planner-help-btn:hover,
           .planner-help-btn.open {
             background-color: rgba(207,226,255,0.97) !important;
             border-color: #9ec5fe !important;
             border-bottom-color: rgba(207,226,255,0.97) !important;
             color: #495057 !important;
           }
           @media (min-width: 900px) {
             .planner-help-panel { right: 30%; }
             .toolbar-right { margin-right: calc(30% - 16px); }
           }
         "]

         [:div.planner-container
          {:style {:display "flex"
                   :flex-direction "column"
                   :height "100vh"
                   :width "100vw"
                   :overflow "hidden"
                   :position "fixed"
                   :top 0
                   :left 0
                   :background "#fff"
                   :z-index 1000}}

          ;; Toolbar
          [:div.planner-toolbar
           {:style {:display "flex"
                    :align-items "center"
                    :justify-content "space-between"
                    :padding "0px 16px"
                    :border-bottom "1px solid #ddd"
                    :background-color "#f8f9fa"
                    :min-height "50px"
                    :flex-shrink 0}}
           [:div.toolbar-left
            {:style {:display "flex"
                     :align-items "center"
                     :flex-wrap "nowrap"
                     :white-space "nowrap"
                     :min-width "0"
                     :gap "8px"}}
            ;; Exit planner button (always first)
            [:button.btn.btn-sm.btn-outline-secondary
             {:on-click (fn []
                          (let [project-id @(rf/subscribe [:nav/match :parameters :query :project-id])
                                site-id    @(rf/subscribe [:nav/match :parameters :query :site-id])]
                            (cond
                              (and project-id site-id)
                              (rfe/push-state :site {:project-id project-id :site-id site-id})
                              project-id
                              (rfe/push-state :project {:project-id project-id})
                              :else
                              (rfe/push-state :projects))))}
             "‹ Exit planner"]
            ;; Separator
            [:span {:style {:border-left "1px solid #dee2e6"
                            :height "24px"}}]
            (cond
              ;; Scale setting action
              (= action "set-scale")
              (let [n (count scale-pts)]
                (cond
                  ;; Confirmation state: calculated scale awaiting confirm/cancel
                  pending-ppm
                  [:div {:style {:display "flex" :align-items "center" :gap "12px"}}
                   [:span.text-muted (str "Calculated scale: " (.toFixed pending-ppm 1) " px/m")]
                   [:button.btn.btn-sm.btn-success
                    {:on-click (fn []
                                 (rf/dispatch [::e/set-scale-distance
                                               {:scale-points  @scale-points
                                                :scale-distance @scale-distance
                                                :img-width (or img-width 800)
                                                :img-height (or img-height 600)
                                                :asset background-asset}])
                                 (reset! scale-points [])
                                 (reset! scale-distance nil)
                                 (reset! pending-scale-ppm nil)
                                 (rf/dispatch [:nav/update-query dissoc :action]))}
                    "Confirm"]
                   [:button.btn.btn-sm.btn-outline-secondary
                    {:on-click (fn []
                                 (reset! scale-points [])
                                 (reset! scale-distance nil)
                                 (reset! pending-scale-ppm nil))}
                    "Cancel"]]

                  ;; Two points set: show distance input
                  (= n 2)
                  [:div {:style {:display "flex" :align-items "center" :gap "8px"}}
                   [:span.text-muted "Distance between points:"]
                   [:input.form-control.form-control-sm
                    {:type "number"
                     :step "0.01"
                     :min "0.01"
                     :placeholder "meters"
                     :value @scale-distance
                     :style {:width "120px"}
                     :on-change #(reset! scale-distance (js/parseFloat (.. % -target -value)))}]
                   [:span.text-muted "m"]
                   [:button.btn.btn-sm.btn-primary
                    {:disabled (not @scale-distance)
                     :on-click (fn []
                                 (when @scale-distance
                                   (let [ppm (planner-utils/pixels-per-meter scale-pts @scale-distance)]
                                     (reset! pending-scale-ppm ppm))))}
                    "Set Scale"]
                   [:button.btn.btn-sm.btn-outline-secondary
                    {:on-click (fn []
                                 (reset! scale-points [])
                                 (reset! scale-distance nil))}
                    "Start over"]]

                  ;; One point set
                  (= n 1)
                  [:div {:style {:display "flex" :align-items "center" :gap "8px" :color "#495057"}}
                   [icon :info-circle-fill]
                   [:span "Click a second point on the image to complete the measurement."]]

                  ;; No points yet
                  :else
                  [:div {:style {:display "flex" :align-items "center" :gap "8px" :color "#495057"}}
                   [icon :info-circle-fill]
                   [:span "Scale must be set. Click two points on the image to set the scale. Scroll to zoom, keep right mouse button pressed to pan"]]))

              ;; Simulation paths container selected
              (= selected-type "routes-container")
              (let [selected-building-id (:building-id @selected-node)]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 [:span.text-muted "Simulation paths"]
                 [:button.btn.btn-sm.btn-primary
                  {:on-click (fn []
                               (let [new-route-name (planner-utils/generate-route-name current-level-routes)
                                     ;; Calculate initial points based on current image dimensions
                                     ;; Offset each new route to avoid overlap
                                     route-index (count current-level-routes)
                                     w (or img-width 800)
                                     h (or img-height 600)
                                     ;; Base size is 1/4 of image, grow by 5% per route
                                     base-size 0.25
                                     size-increment 0.05
                                     size-factor (+ base-size (* route-index size-increment))
                                     ;; Cap at 60% of image size
                                     size-factor (min size-factor 0.6)
                                     rect-w (* w size-factor)
                                     rect-h (* h size-factor)
                                     ;; Offset to the right by 20px per route
                                     x-offset (* route-index 20)
                                     ;; Center vertically, but offset horizontally
                                     start-x (+ (- (/ w 2) (/ rect-w 2)) x-offset)
                                     start-y (- (/ h 2) (/ rect-h 2))
                                     ;; Clamp to stay within image bounds
                                     start-x (min start-x (- w rect-w 10))
                                     initial-points [[start-x start-y]
                                                     [(+ start-x rect-w) start-y]
                                                     [(+ start-x rect-w) (+ start-y rect-h)]
                                                     [start-x (+ start-y rect-h)]]]
                                 (reset! current-route-points initial-points)
                                 (rf/dispatch [::e/add-route plan-id selected-building-id selected-level-id new-route-name initial-points])))}
                  [icon :plus]
                  " Add simulation path"]])

              (= selected-type "backgrounds-container")
              [:div {:style {:display "flex"
                             :align-items "center"
                             :gap "12px"}}
               [:span.text-muted "Background images"]
               [:button.btn.btn-sm.btn-primary
                {:on-click (fn [] (reset! edit-panel-open? true))}
                [icon :plus]
                " Add background image"]]

              (= selected-type "background")
              (let [selected-background-id (or (:data-id @selected-node) selected-background-id)
                    selected-building-id (or (:building-id @selected-node) selected-building-id)
                    background-asset (some (fn [[_ building]]
                                             (when-let [level (get-in building [:levels selected-level-id])]
                                               (get-in level [:siteresource-backgroundimages selected-background-id])))
                                           (:buildings (:venue (:imdf plan))))
                    all-level-bgs-toolbar  (sort-by (comp #(or (:sort-order %) 0) second)
                                                    (some (fn [[_ building]]
                                                            (when-let [level (get-in building [:levels selected-level-id])]
                                                              (:siteresource-backgroundimages level)))
                                                          (:buildings (:venue (:imdf plan)))))
                    background-name (:name background-asset)
                    is-autodetected (:autodetected background-asset)
                    has-scale (and (some? pixels-per-meter) (not= pixels-per-meter 100))]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 (if (> (count all-level-bgs-toolbar) 1)
                   [:select.form-select.form-select-sm
                    {:style {:width "160px"}
                     :value (or selected-background-id "")
                     :on-change (fn [e]
                                  (let [new-id (.. e -target -value)]
                                    (rf/dispatch [:nav/update-query
                                                  (fn [q] (-> q
                                                              (assoc :background-id new-id)
                                                              (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])))}
                      (for [[idx [bg-id bg-asset]] (map-indexed vector (reverse all-level-bgs-toolbar))]
                        [:option {:key bg-id :value bg-id}
                         (str (inc idx) ". " (or (:image-url bg-asset) "Image"))])]
                   [:span.text-muted (or background-name "Background image")])
                 [:button.btn.btn-sm.btn-outline-secondary
                  {:title "Set image scale by clicking two points"
                   :on-click (fn []
                               (reset! scale-points [])
                               (reset! scale-distance nil)
                               (rf/dispatch [:nav/update-query (fn [q] (-> q
                                                                           (assoc :action "set-scale")))]))}
                  "Set Scale"]

                 [:button
                  {:title (if bg-move-mode? "Cancel move" "Move background image on the map")
                   :class (if bg-move-mode? "btn btn-sm btn-primary" "btn btn-sm btn-outline-secondary")
                   :on-click (fn []
                               (reset! bg-rotate-active nil)
                               (if bg-move-mode?
                                 (reset! bg-move-active nil)
                                 (reset! bg-move-active selected-background-id)))}
                  [icon :arrows-move]
                  (if bg-move-mode? " Moving…" " Move")]

                 [:button
                  {:title    "Rotate 90° counter-clockwise"
                   :class    "btn btn-sm btn-outline-secondary"
                   :on-click (fn []
                               (let [current (or (:rotation-deg background-asset) 0)
                                     new-rot (mod (- current 90) 360)]
                                 (rf/dispatch [::e/update-background-rotation
                                               {:background-id selected-background-id
                                                :asset-id      (:asset-id background-asset)
                                                :rotation-deg  new-rot}])))}
                  "Rotate left"]

                 [:button
                  {:title    "Rotate 90° clockwise"
                   :class    "btn btn-sm btn-outline-secondary"
                   :on-click (fn []
                               (let [current (or (:rotation-deg background-asset) 0)
                                     new-rot (mod (+ current 90) 360)]
                                 (rf/dispatch [::e/update-background-rotation
                                               {:background-id selected-background-id
                                                :asset-id      (:asset-id background-asset)
                                                :rotation-deg  new-rot}])))}
                  "Rotate right"]

                 ;; Auto-detect button (only if scale is set)
                 (when has-scale
                   [:button.btn.btn-sm.btn-primary
                    {:title (if is-autodetected
                              "Remove existing auto-detected units and openings, then re-detect"
                              "Automatically detect units and openings from floorplan")
                     :on-click (fn []
                                 (rf/dispatch [::e/auto-detect-units-openings
                                               plan-id
                                               selected-building-id
                                               selected-level-id
                                               selected-background-id]))}
                    (if is-autodetected
                      "Re-detect units & openings"
                      "Auto-detect units & openings")])
                 ;; Delete button
                 (if (= @delete-confirm-id selected-background-id)
                   [:div {:style {:display "flex" :align-items "center" :gap "6px"}}
                    [:span.text-muted.small "Delete?"]
                    [:button.btn.btn-sm.btn-danger
                     {:on-click (fn []
                                  (rf/dispatch [:nav/update-query dissoc :background-id])
                                  (reset! selected-node nil)
                                  (reset! bg-move-active nil)
                                  (reset! bg-rotate-active nil)
                                  (reset! delete-confirm-id nil)
                                  (rf/dispatch [::e/delete-background plan-id selected-building-id selected-level-id selected-background-id]))}
                     "Yes, delete"]
                    [:button.btn.btn-sm.btn-outline-secondary
                     {:on-click #(reset! delete-confirm-id nil)}
                     "Cancel"]]
                   [:button.btn.btn-sm.btn-outline-danger
                    {:title "Delete background image"
                     :on-click (fn []
                                 (reset! bg-move-active nil)
                                 (reset! bg-rotate-active nil)
                                 (reset! delete-confirm-id selected-background-id))}
                    [icon :trash-fill]])])

              ;; Route (simulation path) selected - editing is always enabled
              (= selected-type "route")
              (let [selected-route-id (or (:data-id @selected-node) route-id-from-url)
                    selected-route-building-id (or (:building-id @selected-node) selected-building-id)
                    route-name-display (some (fn [[_ building]]
                                               (when-let [level (get-in building [:levels selected-level-id])]
                                                 (:name (get-in level [:routes selected-route-id]))))
                                             (:buildings (:venue (:imdf plan))))]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 [:span.text-muted (or route-name-display "Simulation path")]
                 [:button.btn.btn-sm.btn-outline-danger
                  {:title "Delete simulation path"
                   :on-click (fn []
                               (when (js/confirm (str "Delete simulation path \"" (or route-name-display selected-route-id) "\"?"))
                                 (reset! selected-node nil)
                                 (reset! current-route-points [])
                                 (rf/dispatch [:nav/update-query dissoc :route-id])
                                 (rf/dispatch [::e/delete-route plan-id selected-route-building-id selected-level-id selected-route-id])))}
                  [icon :trash-fill]]
                 [:span.text-muted.small {:style {:border-left "1px solid #dee2e6"
                                                  :padding-left "12px"}}
                  "Drag corners to reshape • Drag path to move • Alt+click to add point • Alt+right click to remove"]])

              ;; Geofences container selected
              (= selected-type "geofences-container")
              (let [selected-building-id (:building-id @selected-node)
                    current-level-geofences (get-in plan [:imdf :venue :buildings selected-building-id :levels selected-level-id :geofences])]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 [:span.text-muted "Geofences"]
                 [:button.btn.btn-sm.btn-primary
                  {:on-click (fn []
                               (let [new-geofence-name (planner-utils/generate-geofence-name current-level-geofences)
                                     ;; Calculate initial points based on current image dimensions
                                     ;; Offset each new geofence to avoid overlap
                                     geofence-index (count current-level-geofences)
                                     w (or img-width 800)
                                     h (or img-height 600)
                                     ;; Base size is 1/4 of image, grow by 5% per geofence
                                     base-size 0.25
                                     size-increment 0.05
                                     size-factor (+ base-size (* geofence-index size-increment))
                                     ;; Cap at 60% of image size
                                     size-factor (min size-factor 0.6)
                                     rect-w (* w size-factor)
                                     rect-h (* h size-factor)
                                     ;; Offset to the right by 20px per geofence
                                     x-offset (* geofence-index 20)
                                     ;; Center vertically, but offset horizontally
                                     start-x (+ (- (/ w 2) (/ rect-w 2)) x-offset)
                                     start-y (- (/ h 2) (/ rect-h 2))
                                     ;; Clamp to stay within image bounds
                                     start-x (min start-x (- w rect-w 10))
                                     initial-points [[start-x start-y]
                                                     [(+ start-x rect-w) start-y]
                                                     [(+ start-x rect-w) (+ start-y rect-h)]
                                                     [start-x (+ start-y rect-h)]]]
                                 (reset! current-geofence-points initial-points)
                                 (rf/dispatch [::e/add-geofence plan-id selected-building-id selected-level-id new-geofence-name initial-points])))}
                  [icon :plus]
                  " Add geofence"]])

              ;; Geofence selected - editing is always enabled
              (= selected-type "geofence")
              (let [selected-geofence-id (or (:data-id @selected-node) geofence-id-from-url)
                    selected-geofence-building-id (or (:building-id @selected-node) selected-building-id)
                    geofence (some (fn [[_ building]]
                                     (when-let [level (get-in building [:levels selected-level-id])]
                                       (get-in level [:geofences selected-geofence-id])))
                                   (:buildings (:venue (:imdf plan))))
                    geofence-name-display (:name geofence)
                    is-tracking-area (:use-as-tracking-area geofence)]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 [:span.text-muted (or geofence-name-display "Geofence")]
                 ;; Toggle tracking area button
                 (if is-tracking-area
                   [:button.btn.btn-sm.btn-outline-secondary
                    {:title "Remove tracking area designation"
                     :on-click #(rf/dispatch [::e/toggle-geofence-tracking-area plan-id selected-geofence-building-id selected-level-id selected-geofence-id false])}
                    "Unset as tracking area"]
                   [:button.btn.btn-sm.btn-primary
                    {:title "Designate this geofence as a tracking area"
                     :on-click #(rf/dispatch [::e/toggle-geofence-tracking-area plan-id selected-geofence-building-id selected-level-id selected-geofence-id true])}
                    "Set as tracking area"])
                 ;; Distribute locators button (only if tracking area is set)
                 (when is-tracking-area
                   [:button.btn.btn-sm.btn-primary
                    {:title "Automatically distribute locators in this tracking area"
                     :on-click #(rf/dispatch [::e/distribute-locators plan-id selected-geofence-building-id selected-level-id selected-geofence-id])}
                    [icon :grid-3x3-gap-fill]
                    " Distribute locators"])
                 [:button.btn.btn-sm.btn-outline-danger
                  {:title "Delete geofence"
                   :on-click (fn []
                               (when (js/confirm (str "Delete geofence \"" (or geofence-name-display selected-geofence-id) "\"?"))
                                 (reset! selected-node nil)
                                 (reset! current-geofence-points [])
                                 (rf/dispatch [:nav/update-query dissoc :geofence-id])
                                 (rf/dispatch [::e/delete-geofence plan-id selected-geofence-building-id selected-level-id selected-geofence-id])))}
                  [icon :trash-fill]]
                 [:span.text-muted.small {:style {:border-left "1px solid #dee2e6"
                                                  :padding-left "12px"}}
                  "Drag corners to reshape • Drag path to move • Alt+click to add point • Alt+right click to remove"]])

              ;; Units container selected
              (= selected-type "units-container")
              (let [selected-building-id (:building-id @selected-node)
                    current-level-units (get-in plan [:imdf :venue :buildings selected-building-id :levels selected-level-id :units])]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 [:span.text-muted "Units"]
                 [:button.btn.btn-sm.btn-primary
                  {:on-click (fn []
                               (let [new-unit-name (planner-utils/generate-unit-name current-level-units)
                                     ;; Calculate initial points based on current image dimensions
                                     ;; Offset each new unit to avoid overlap
                                     unit-index (count current-level-units)
                                     w (or img-width 800)
                                     h (or img-height 600)
                                     ;; Base size is 1/4 of image, grow by 5% per unit
                                     base-size 0.25
                                     size-increment 0.05
                                     size-factor (+ base-size (* unit-index size-increment))
                                     ;; Cap at 60% of image size
                                     size-factor (min size-factor 0.6)
                                     rect-w (* w size-factor)
                                     rect-h (* h size-factor)
                                     ;; Offset to the right by 20px per unit
                                     x-offset (* unit-index 20)
                                     ;; Center vertically, but offset horizontally
                                     start-x (+ (- (/ w 2) (/ rect-w 2)) x-offset)
                                     start-y (- (/ h 2) (/ rect-h 2))
                                     ;; Clamp to stay within image bounds
                                     start-x (min start-x (- w rect-w 10))
                                     initial-points [[start-x start-y]
                                                     [(+ start-x rect-w) start-y]
                                                     [(+ start-x rect-w) (+ start-y rect-h)]
                                                     [start-x (+ start-y rect-h)]]]
                                 (reset! current-unit-points initial-points)
                                 (rf/dispatch [::e/add-unit plan-id selected-building-id selected-level-id new-unit-name initial-points])))}
                  [icon :plus]
                  " Add unit"]])

              ;; Unit selected - editing is always enabled
              (= selected-type "unit")
              (let [selected-unit-id (or (:data-id @selected-node) unit-id-from-url)
                    selected-unit-building-id (or (:building-id @selected-node) selected-building-id)
                    unit-name-display (some (fn [[_ building]]
                                              (when-let [level (get-in building [:levels selected-level-id])]
                                                (:name (get-in level [:units selected-unit-id]))))
                                            (:buildings (:venue (:imdf plan))))]
                [:div {:style {:display "flex"
                               :align-items "center"
                               :gap "12px"}}
                 [:span.text-muted (or unit-name-display "Unit")]
                 [:button.btn.btn-sm.btn-outline-danger
                  {:title "Delete unit"
                   :on-click (fn []
                               (when (js/confirm (str "Delete unit \"" (or unit-name-display selected-unit-id) "\"?"))
                                 (reset! selected-node nil)
                                 (reset! current-unit-points [])
                                 (rf/dispatch [:nav/update-query dissoc :unit-id])
                                 (rf/dispatch [::e/delete-unit plan-id selected-unit-building-id selected-level-id selected-unit-id])))}
                  [icon :trash-fill]]
                 [:span.text-muted.small {:style {:border-left "1px solid #dee2e6"
                                                  :padding-left "12px"}}
                  "Drag corners to reshape • Drag path to move • Alt+click to add point • Alt+right click to remove"]])

              ;; Default toolbar content (no action or unknown action)
              :else
              [:div {:style {:display "flex"
                             :align-items "center"
                             :gap "12px"}}
               [:h4.m-0 (or @(rf/subscribe [::su/site-plan plan-id :nodes "root" :properties :name])
                            "Site plan")]
               [:button.btn.btn-sm.btn-primary
                {:on-click #(rf/dispatch [::e/save-plan plan-id])}
                "Save"]])]
           [:div.toolbar-right
            {:style {:display "flex"
                     :align-items "flex-end"
                     :align-self "stretch"}}
            (when help-content
              [:button.btn.btn-sm.btn-outline-secondary.planner-help-btn
               {:class (when @help-open? "open")
                :style {:display "flex"
                        :align-items "center"
                        :gap "4px"}
                :title (if @help-open? "Close help" "Open help")
                :on-click #(swap! help-open? not)}
               [icon :question-circle]
               [icon (if @help-open? :chevron-down :chevron-right)]])]]

          ;; Main content area
          [:div.planner-main
           {:style {:display "flex"
                    :flex "1"
                    :overflow "hidden"}}

           ;; Left sidebar - Tree view
           [:div.planner-sidebar
            {:style {:width "250px"
                     :border-right "1px solid #ddd"
                     :overflow-y "auto"
                     :background-color "#fff"
                     :padding "12px"
                     :flex-shrink 0}}
            [tree/imdf-tree plan-id
             (fn [selection-info]
               ;; Clear all polygon point atoms when selection changes (so new polygon's points get loaded)
               (reset! current-route-points [])
               (reset! current-geofence-points [])
               (reset! current-unit-points [])
               (reset! selected-node-type (:data-type selection-info))
               (reset! selected-node selection-info))
             (:key @selected-node)]]

           ;; Map view
           [:div.site-plan__map
            {:style {:flex "1"
                     :overflow "hidden"
                     :position "relative"
                     :background "#f5f5f5"}}
            ;; Help panel — right-aligned to match toolbar button, opens on top of map
            (when (and help-content @help-open?)
              [:div {:class "planner-help-panel"
                     :style {:position "absolute"
                             :top 0
                             :left 0
                             :z-index 100
                             :max-height "50%"
                             :overflow-y "auto"
                             :background "rgba(207,226,255,0.97)"
                             :border "1px solid #9ec5fe"
                             :border-radius "0 0 4px 4px"
                             :padding "12px 16px"
                             :font-size "14px"
                             :line-height "1.5"
                             :box-shadow "0 2px 8px rgba(0,0,0,0.15)"}}
               [:p {:style {:margin 0}} help-content]])
            ;; Edit panel — anchored to right side, only shown for editable elements
            [edit-panel edit-panel-open? selected-type selected-id plan-id selected-building-id selected-level-id]
            [map-viewport selected-node image-dimensions plan-id]]]]]))))

(defn plan-list []
  [:<>
   [:div.d-flex.flex-row.align-items-center.justify-content-between.mb-3
    [:h3.m-0 "Planner"]

    [:a.btn.btn-primary
     {:type "button"
      :href "#"
      :on-click (fn [e]
                  (.preventDefault e)
                  (rf/dispatch [::e/init-new-plan]))}
     "Create site plan"]]

   [:table
    [:thead
     [:tr
      [:th "Name"]
      [:th "Created"]]]
    [:tbody
     (for [plan (:result @(q/subscribe e/plans-query {}))]
       [:tr
        {:key (:id plan)
         :on-click #(rfe/push-state :planner-site-plan {:plan-id (:id plan)})}
        [:th (:name plan)]
        [:th (str (:created plan))]])]]])

(defn page []
  (case @(rf/subscribe [:nav/match :data :name])
    :planner-new-plan [plan-edit :new]
    :planner-site-plan [plan-edit @(rf/subscribe [:nav/match :parameters :path :plan-id])]
    ;; Only show wrapper for plan-list, not for planner edit view
    [:section.section.d-flex.flex-column.flex-grow-1
     [:div.d-flex.flex-row.flex-grow-1
      [:div.content.d-flex.flex-column.flex-grow-1.p-4
       [:div.d-flex.flex-column.flex-grow-1
        [plan-list]]]]]))

(def list-controller
  {:id :planner-list
   :subscriptions (fn [_match]
                    [[:planner/new-plan]])
   :start (fn [_params]
            (q/execute e/plans-query {}))})

(def new-plan-controller
  {:id :planner-new-plan
   :start (fn [])})

(def plan-controller
  {:id :planner-plan
   :parameters {:path [:plan-id]}
   :subscriptions (fn [match]
                    ;; apply-subscriptions passes the full match (not the filtered identity),
                    ;; so extract plan-id via parameters.path, not from the path string.
                    (when-let [plan-id (some-> match :parameters :path :plan-id)]
                      [[:planner/plan-updated {:id (uuid plan-id)}]]))
   :start (fn [{{:keys [plan-id]} :path}]
            (q/execute e/plan-query {:id plan-id} {:on-success [::e/plan-loaded plan-id]}))})
